import { StationSource } from './../core/models/station-source';
import { TimezoneEnum } from './../shared/model/time-zone.enum';
import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
export interface Filter {
  filterName?: string;
  keywords?: string[];
  markets?: string[];
  stations?: string[];
  formats?: string[];
}

export interface ProfileConfig {
  default?: {
    markets?: string[], // Default Market names
    stationFilter?: string, // Default Home Filter name
    searchFilter?: string, // Default Search Filter name
    timezoneType?: TimezoneEnum, // Default Timezone
    stationSource?: StationSource // Default Station Source
  };
  filter?: {
    search?: Filter[],
    stations?: Filter[]
  };
  favorite?: {
    stations: string[]
  };
}

export enum Role {
  STANDARD = 'STANDARD',
  ADMIN = 'ADMIN'
}

export interface ProfileRole {
  profileAccess: Role;
}

export interface Profile {
  token: string;
  userName: string;
  configuration: ProfileConfig;
  role: ProfileRole;
}



@Injectable({
  providedIn: 'root',
})
export class ProfileModelService {

  private _isAuthenticated: boolean;
  private _profile: Profile;
  public profileUpdated: BehaviorSubject<boolean>;

  constructor() {
    // initialize for tests
    this._profile = {
      token: null,
      userName: null,
      configuration: null,
      role: null
    };
    this.profileUpdated = new BehaviorSubject<boolean>(false);
  }

  set profile(profile: Profile) {
    this._profile = profile;
    this._isAuthenticated = true;
    this.profileUpdated.next(true);
  }

  getProfile() {
    return this._profile;
  }

  get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  get token(): string {
    return this._profile.token;
  }

  get configuration(): ProfileConfig {
    return this._profile.configuration;
  }


  private hasConfiguration(): boolean {
    return this._profile.configuration != null;
  }

  private hasDefault(): boolean {
    return this.hasConfiguration() && this._profile.configuration.default != null;
  }
  private hasFilter(): boolean {
    return this.hasConfiguration() && this._profile.configuration.filter != null;
  }

  private hasSearch(): boolean {
    return this.hasFilter() && this._profile.configuration.filter.search != null;
  }

  private hasStationFilters(): boolean {
    return this.hasConfiguration() && this.hasFilter() && this._profile.configuration.filter.stations != null;
  }

  private hasFavorite(): boolean {
    return this.hasConfiguration() && this._profile.configuration.favorite != null;
  }

  public getSearchFilter(): Filter[] {
    if (this.hasSearch()) {
      return this._profile.configuration.filter.search.filter(
        ((filter: Filter) => (filter.filterName && (filter.filterName.length > 0)))
      );
    }

    return [];
  }

  public getDefaultMarkets(): string[] {
    if (this.hasDefault() && this._profile.configuration.default.markets != null) {
      return this._profile.configuration.default.markets;
    }

    return [];
  }

  public setDefaultMarkets(markets: string[]): void {
    if (this.hasConfiguration()) {
      if (this.hasDefault()) {
        this._profile.configuration.default.markets = markets;
      } else {
        this._profile.configuration.default = {
          markets
        };
      }
    } else {
      this._profile.configuration = {
        default: {
          markets
        }
      };
    }
  }

  public getDefaultSearchFilter(): Filter {
    if (this.hasDefault() && this._profile.configuration.default.searchFilter != null) {
      const searchFilter: Filter = this.getSearchFilter()
        .find((filter: Filter) => filter.filterName === this._profile.configuration.default.searchFilter);

      if (searchFilter) {
        return searchFilter;
      }
    }

    return {
      filterName: '',
      keywords: [],
      markets: [],
      stations: []
    } as Filter;
  }

  public setDefaultSearchFilter(filterName: string): void {
    if (this.hasConfiguration()) {
      if (this.hasDefault()) {
        this._profile.configuration.default.searchFilter = filterName;
      } else {
        this._profile.configuration.default = {
          searchFilter: filterName
        };
      }
    } else {
      this._profile.configuration = {
        default: {
          searchFilter: filterName
        }
      };
    }
  }

  public getDefaultStationFilter(): Filter {
    if (this.hasDefault() && this._profile.configuration.default.stationFilter != null) {
      const stationFilter: Filter = this.getStationFilters()
        .find((filter: Filter) => filter.filterName === this._profile.configuration.default.stationFilter);

      if (stationFilter) {
        return stationFilter;
      }
    }

    return {
      filterName: '',
      markets: [],
      formats: [],
      stations: []
    } as Filter;
  }

  public setDefaultStationFilter(filterName: string): void {
    if (this.hasConfiguration()) {
      if (this.hasDefault()) {
        this._profile.configuration.default.stationFilter = filterName;
      } else {
        this._profile.configuration.default = {
          stationFilter: filterName
        };
      }
    } else {
      this._profile.configuration = {
        default: {
          stationFilter: filterName
        }
      };
    }
  }

  public getDefaultTimezoneType(): TimezoneEnum {
    if (this.hasDefault() && this._profile.configuration.default.timezoneType) {
      return this._profile.configuration.default.timezoneType;
    }
    /* If the user does not have a default timezone saved in his profile settings,
        it should use the station timezone as default */
    return TimezoneEnum.STATION;
  }

  public getDefaultStationSource(): StationSource {
    if (this.hasDefault() && this._profile.configuration.default.stationSource) {
      const source = this._profile.configuration.default.stationSource as StationSource;
      // Validation made for the reset object
      if (source.id === -1){
        return null;
      } else {
        return source;
      }
    }

    // If the user does not have a default audio source saved in his profile settings
    return null;
  }

  public setDefaultTimezoneType(timezoneType: TimezoneEnum): void {
    if (this.hasConfiguration()) {
      if (this.hasDefault()) {
        this._profile.configuration.default.timezoneType = timezoneType;
      } else {
        this._profile.configuration.default = {
          timezoneType
        };
      }
    } else {
      this._profile.configuration = {
        default: {
          timezoneType
        }
      };
    }
  }

  public setDefaultStationSource(stationSource: StationSource): void {
    if (this.hasConfiguration()) {
      this._profile.configuration.default = { ...this._profile.configuration.default, stationSource };
    } else {
      this._profile.configuration = { default: { stationSource } };
    }
  }

  public findSearchFilterByName(name: string): Filter {
    const currentSearch = this.getSearchFilter();
    if (currentSearch.length > 0) {
      const filteredList = currentSearch.filter((filter: Filter) => filter.filterName === name);
      return filteredList.length > 0 ? filteredList[0] : null;
    } else {
      return null;
    }
  }

  public deleteSearchFilterByName(name: string): void {
    const currentSearchFilters = this._profile.configuration.filter.search;
    this._profile.configuration.filter.search = currentSearchFilters.filter(
      (filter: Filter) => (filter.filterName !== name) && filter.filterName && (filter.filterName.length > 0)
    );
  }

  public addSearchFilter(filter: Filter): boolean {
    if (this.hasConfiguration()) {
      if (this.hasFilter()) {
        if (this.hasSearch()) {
          const isFilterExists = this.findSearchFilterByName(filter.filterName);
          if (!isFilterExists) {
            this._profile.configuration.filter.search.push(filter);
            this._profile.configuration.filter.search = this._profile.configuration.filter.search.filter(
              ((f: Filter) => (f.filterName && (f.filterName.length > 0)))
            );
            return true;
          } else {
            return false;
          }
        } else {
          this._profile.configuration.filter.search = [].concat(filter);
          return true;
        }
      } else {
        this._profile.configuration.filter = {
          search: [].concat(filter)
        };
        return true;
      }
    } else {
      this._profile.configuration = {
        filter: {
          search: [].concat(filter)
        }
      };
    }

    return true;
  }

  public getStationFilters(): Filter[] {
    if (this.hasStationFilters()) {
      return this._profile.configuration.filter.stations.filter(
        ((filter: Filter) => (filter.filterName && (filter.filterName.length > 0)))
      );
    }
    return [];
  }

  public findStationFilterByName(name: string): Filter {
    const currentFilters = this.getStationFilters();
    if (currentFilters.length > 0) {
      const filteredList = currentFilters.filter((filter: Filter) => filter.filterName === name);
      return filteredList.length > 0 ? filteredList[0] : null;
    } else {
      return null;
    }
  }

  public deleteStationFilterByName(name: string): void {
    const currentStationFilters = this._profile.configuration.filter.stations;
    this._profile.configuration.filter.stations = currentStationFilters.filter((filter: Filter) => filter.filterName !== name);
  }

  public addStationFilter(filter: Filter): boolean {
    if (this.hasConfiguration()) {
      if (this.hasFilter()) {
        if (this.hasStationFilters()) {
          const isFilterExists = this.findStationFilterByName(filter.filterName);
          if (!isFilterExists) {
            this._profile.configuration.filter.stations.push(filter);
            return true;
          } else {
            return false;
          }
        } else {
          this._profile.configuration.filter.stations = [].concat(filter);
          return true;
        }
      } else {
        this._profile.configuration.filter = {
          stations: [].concat(filter)
        };
        return true;
      }
    } else {
      this._profile.configuration = {
        filter: {
          stations: [].concat(filter)
        }
      };
      return true;
    }
  }

  public getFavoriteStations(): string[] {
    if (this.hasFavorite() && this._profile.configuration.favorite.stations) {
      return this._profile.configuration.favorite.stations;
    }

    return [];
  }

  public setFavoriteStations(favorites: string[]): void {
    if (this.hasConfiguration()) {
      this._profile.configuration.favorite = {
        stations: favorites
      };
    } else {
      this._profile.configuration = {
        favorite: {
          stations: favorites
        }
      };
    }
  }

}
